<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text class="orangehrm-main-title" tag="h6">
        {{ $t('payroll.add_deduction_rule') }}
      </oxd-text>
      <oxd-divider />
      <oxd-form :loading="isLoading" @submit-valid="onSave">
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.deductionName"
                :rules="rules.name"
                :label="$t('payroll.deduction_name')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.deductionType"
                type="select"
                :rules="rules.type"
                :options="typeOptions"
                :label="$t('payroll.deduction_type')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="isTaxDeduction">
              <oxd-input-field
                v-model="rule.calculationMethod"
                type="select"
                :options="calculationMethodOptions"
                :label="$t('payroll.calculation_method')"
              />
            </oxd-grid-item>
            <oxd-grid-item v-else-if="isLateDeduction">
              <oxd-input-field
                v-model="rule.calculationMethod"
                type="select"
                :options="lateCalculationMethodOptions"
                :label="$t('payroll.calculation_method')"
              />
            </oxd-grid-item>
            <oxd-grid-item v-else-if="!isLateDeduction">
              <oxd-input-field
                v-model="rule.amount"
                :rules="amountFieldRules"
                :label="$t('payroll.deduction_amount')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row v-if="isLateDeduction">
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.isPercentage"
                type="select"
                :rules="rules.percentage"
                :options="percentageOptions"
                :label="$t('payroll.percentage')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="rule.isPercentage?.param === 'yes'">
              <oxd-input-field
                v-model="rule.percentageBase"
                type="select"
                :options="latePercentageBaseOptions"
                :label="$t('payroll.percentage_base')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="showLateAmountField">
              <oxd-input-field
                v-model="rule.amount"
                :rules="amountFieldRules"
                :label="$t('payroll.deduction_amount')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="showCurrencyField">
              <oxd-input-field
                v-model="rule.currencyId"
                type="select"
                :rules="rules.currency"
                :options="currencies"
                :label="$t('payroll.currency_id')"
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="showLatePayGradeSelect">
              <oxd-input-field
                v-model="latePayGradeNominalSource"
                type="select"
                :options="latePayGradeNominalSourceOptions"
                :label="pt('payroll.late_pay_grade_nominal_source')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="showLatePayGradeSelect">
              <oxd-input-field
                v-model="lateConfig.payGradeIds"
                type="multiselect"
                :label="pt('payroll.late_pay_grades')"
                :options="payGradeOptions"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.isActive"
                type="select"
                :rules="rules.active"
                :options="activeOptions"
                :label="$t('payroll.active')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row v-if="isLateDeduction">
          <div
            class="orangehrm-form-field-half-lg orangehrm-form-field-half-lg--late-schedule"
          >
            <late-deduction-schedule-editor
              v-model="lateConfig"
              :is-percentage="rule.isPercentage?.param === 'yes'"
              :show-bands="isLateTiered"
            />
          </div>
        </oxd-form-row>
        <oxd-form-row v-if="!isTaxDeduction && !isLateDeduction">
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.isPercentage"
                type="select"
                :rules="rules.percentage"
                :options="percentageOptions"
                :label="$t('payroll.percentage')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="rule.isPercentage?.param === 'yes'">
              <oxd-input-field
                v-model="rule.percentageBase"
                type="select"
                :options="percentageBaseOptions"
                :label="$t('payroll.percentage_base')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item v-if="showCurrencyField">
              <oxd-input-field
                v-model="rule.currencyId"
                type="select"
                :rules="rules.currency"
                :options="currencies"
                :label="$t('payroll.currency_id')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.isActive"
                type="select"
                :rules="rules.active"
                :options="activeOptions"
                :label="$t('payroll.active')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row v-if="isTaxTerCalculation">
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.percentageBase"
                type="select"
                :options="percentageBaseOptions"
                :label="$t('payroll.percentage_base')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.nonNpwpSurchargePercent"
                :label="'Non-NPWP surcharge (%)'"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.isActive"
                type="select"
                :rules="rules.active"
                :options="activeOptions"
                :label="$t('payroll.active')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row v-if="isTaxDeduction && requiresDeductionAmount">
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.percentageBase"
                type="select"
                :options="percentageBaseOptions"
                :label="$t('payroll.percentage_base')"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.amount"
                :rules="amountFieldRules"
                :label="$t('payroll.deduction_amount')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.nonNpwpSurchargePercent"
                :label="'Non-NPWP surcharge (%)'"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row v-if="isTaxDeduction && requiresDeductionAmount">
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="rule.isActive"
                type="select"
                :rules="rules.active"
                :options="activeOptions"
                :label="$t('payroll.active')"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row
          v-if="isTaxDeduction && rule.calculationMethod?.param === 'ter'"
          class="orangehrm-ter-preview-row"
        >
          <oxd-grid :cols="1" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <div class="orangehrm-ter-preview">
                <template v-if="activeTerSchedule">
                  <p class="orangehrm-ter-preview__name">
                    {{ activeTerSchedule.name }}
                    <span class="orangehrm-ter-preview__range">
                      ({{ activeTerSchedule.effectiveFrom }} –
                      {{ activeTerSchedule.effectiveTo }})
                    </span>
                  </p>
                </template>
                <p v-else class="orangehrm-ter-preview__empty">
                  No active schedule for today
                </p>
              </div>
              <div class="orangehrm-ter-preview-grid">
                <div
                  v-for="section in terBracketPreviewTables"
                  :key="section.isDaily ? 'daily' : section.key"
                  class="orangehrm-ter-preview-grid__cell"
                >
                  <div class="orangehrm-ter-preview-table-wrap">
                    <table class="orangehrm-ter-table">
                      <caption class="orangehrm-ter-table__caption">
                        {{
                          section.caption
                        }}
                      </caption>
                      <thead>
                        <tr>
                          <th>Lower</th>
                          <th>Upper</th>
                          <th>Rate (%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, idx) in section.rows"
                          :key="`${section.isDaily ? 'd' : section.key}-${idx}`"
                        >
                          <td>
                            {{
                              formatTerNominal(
                                row.lowerBound ?? row.lower_bound,
                              )
                            }}
                          </td>
                          <td>{{ formatTerUpperBound(row) }}</td>
                          <td>
                            {{
                              formatTerRatePercent(
                                row.ratePercent ?? row.rate_percent,
                              )
                            }}
                          </td>
                        </tr>
                        <tr v-if="section.isDaily && section.rows.length === 0">
                          <td colspan="3" class="orangehrm-ter-table-empty">
                            No daily brackets in this schedule
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-row>
          <div class="orangehrm-form-field-half-lg">
            <oxd-input-field
              v-model="rule.comments"
              type="textarea"
              :label="$t('general.comment')"
              :rules="rules.comments"
            />
          </div>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <required-text />
          <div class="orangehrm-button-row">
            <oxd-button
              display-type="ghost"
              :label="$t('general.cancel')"
              @click="onCancel"
            />
            <submit-button />
          </div>
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {navigate} from '@/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import LateDeductionScheduleEditor from '@/orangehrmPayrollPlugin/components/LateDeductionScheduleEditor.vue';
import {payrollT} from '@/orangehrmPayrollPlugin/util/payrollI18n';
import {
  required,
  validSelection,
  digitsOnlyWithDecimalPoint,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';

const defaultLateConfig = () => ({
  graceEndMinutes: 30,
  halfDayMinutesLate: 121,
  payGradeIds: [],
  payGradeNominalSource: 'calculated',
  bands: [
    {fromMinutesLateExclusive: 0, toMinutesLateInclusive: 30, value: 0},
    {fromMinutesLateExclusive: 30, toMinutesLateInclusive: 120, value: 0.05},
  ],
});

const ruleModel = {
  deductionName: '',
  deductionType: null,
  amount: '',
  currencyId: null,
  isPercentage: null,
  isActive: null,
  comments: '',
  percentageBase: null,
  calculationMethod: null,
  nonNpwpSurchargePercent: '20',
};

export default {
  components: {
    'late-deduction-schedule-editor': LateDeductionScheduleEditor,
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/global-deduction-rules',
    );
    const terHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/ter-schedules',
    );
    const payGradeHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/pay-grades',
    );
    return {
      http,
      terHttp,
      payGradeHttp,
    };
  },
  data() {
    return {
      isLoading: false,
      rule: {...ruleModel},
      lateConfig: defaultLateConfig(),
      activeTerSchedule: null,
      currencies: [],
      payGradeOptions: [],
      rules: {
        comments: [shouldNotExceedCharLength(255)],
        name: [required, shouldNotExceedCharLength(100)],
        currency: [
          (value) => this.rule.isPercentage?.param === 'yes' || required(value),
        ],
        type: [required, validSelection],
        percentage: [required, validSelection],
        active: [required, validSelection],
      },
      percentageOptions: [
        {id: 1, param: 'yes', label: this.$t('general.yes')},
        {id: 2, param: 'no', label: this.$t('general.no')},
      ],
      activeOptions: [
        {id: 1, param: 'yes', label: this.$t('general.yes')},
        {id: 2, param: 'no', label: this.$t('general.no')},
      ],
      percentageBaseOptions: [
        {id: 1, param: 'gross', label: 'Gross'},
        {id: 2, param: 'basic_monthly', label: 'Basic monthly'},
        {id: 3, param: 'taxable_income', label: 'Taxable income'},
      ],
      calculationMethodOptions: [
        {id: 1, param: 'ter', label: 'TER'},
        {id: 2, param: 'flat', label: 'Flat'},
      ],
    };
  },
  computed: {
    typeOptions() {
      return [
        {id: 1, param: 'tax', label: this.$t('payroll.tax')},
        {id: 2, param: 'insurance', label: this.$t('payroll.insurance')},
        {id: 3, param: 'pension', label: this.$t('payroll.pension')},
        {id: 4, param: 'other', label: this.$t('payroll.other')},
        {id: 5, param: 'late', label: this.pt('payroll.deduction_type_late')},
      ];
    },
    latePercentageBaseOptions() {
      return [
        {id: 1, param: 'gross', label: 'Gross'},
        {id: 2, param: 'basic_monthly', label: 'Basic monthly'},
        {id: 3, param: 'taxable_income', label: 'Taxable income'},
        {
          id: 4,
          param: 'pay_grades',
          label: this.pt('payroll.percentage_base_pay_grades'),
        },
      ];
    },
    lateCalculationMethodOptions() {
      return [
        {
          id: 1,
          param: 'tiered',
          label: this.pt('payroll.calculation_method_tiered'),
        },
        {
          id: 2,
          param: 'fixed',
          label: this.pt('payroll.calculation_method_fixed'),
        },
      ];
    },
    showCurrencyField() {
      return this.rule.isPercentage?.param !== 'yes';
    },
    isTaxDeduction() {
      return this.rule.deductionType?.param === 'tax';
    },
    isLateDeduction() {
      return this.rule.deductionType?.param === 'late';
    },
    isLateTiered() {
      return (
        this.isLateDeduction && this.rule.calculationMethod?.param === 'tiered'
      );
    },
    showLatePayGradeSelect() {
      return (
        this.isLateDeduction &&
        this.rule.isPercentage?.param === 'yes' &&
        this.rule.percentageBase?.param === 'pay_grades'
      );
    },
    latePayGradeNominalSourceOptions() {
      return [
        {
          id: 1,
          param: 'configured',
          label: this.pt('payroll.late_pay_grade_nominal_configured'),
        },
        {
          id: 2,
          param: 'calculated',
          label: this.pt('payroll.late_pay_grade_nominal_calculated'),
        },
      ];
    },
    latePayGradeNominalSource: {
      get() {
        const param = this.lateConfig.payGradeNominalSource ?? 'calculated';
        return (
          this.latePayGradeNominalSourceOptions.find(
            (item) => item.param === param,
          ) || this.latePayGradeNominalSourceOptions[1]
        );
      },
      set(value) {
        this.lateConfig.payGradeNominalSource = value?.param ?? 'calculated';
      },
    },
    showLateAmountField() {
      return (
        this.isLateDeduction && this.rule.calculationMethod?.param === 'fixed'
      );
    },
    isTaxTerCalculation() {
      return (
        this.isTaxDeduction && this.rule.calculationMethod?.param === 'ter'
      );
    },
    requiresDeductionAmount() {
      return !this.isTaxTerCalculation && !this.isLateDeduction;
    },
    amountFieldRules() {
      return this.requiresDeductionAmount
        ? [required, digitsOnlyWithDecimalPoint]
        : [];
    },
    terMonthlyBracketSections() {
      const s = this.activeTerSchedule?.schedule ?? {};
      const keys = Object.keys(s).filter(
        (k) => k !== 'ptkpMapping' && k !== 'daily',
      );
      const groupKeys = keys.length > 0 ? keys : ['A', 'B', 'C'];
      return groupKeys.map((key) => ({
        key,
        rows: Array.isArray(s[key]) ? s[key] : [],
      }));
    },
    terDailyRows() {
      const s = this.activeTerSchedule?.schedule ?? {};
      return Array.isArray(s.daily) ? s.daily : [];
    },
    terBracketPreviewTables() {
      const schedule = this.activeTerSchedule?.schedule ?? {};
      const mapping = schedule.ptkpMapping ?? {};
      const monthly = this.terMonthlyBracketSections.map((section) => {
        const raw = mapping[section.key];
        const statuses = Array.isArray(raw)
          ? raw.filter((s) => typeof s === 'string' && s.trim() !== '')
          : [];
        const suffix = statuses.length > 0 ? ` (${statuses.join(', ')})` : '';
        return {
          key: section.key,
          rows: section.rows,
          isDaily: false,
          caption: `Group ${section.key}${suffix}`,
        };
      });
      return [
        ...monthly,
        {
          key: 'daily',
          rows: this.terDailyRows,
          isDaily: true,
          caption: 'Daily',
        },
      ];
    },
  },
  watch: {
    'rule.deductionType'(value) {
      if (value?.param === 'tax') {
        this.rule.isPercentage = this.percentageOptions[0];
        this.rule.currencyId = null;
        this.rule.calculationMethod = this.calculationMethodOptions[0];
        if (this.rule.calculationMethod?.param === 'ter') {
          this.rule.amount = 0;
        }
        this.loadActiveTerSchedule();
      } else if (value?.param === 'late') {
        this.rule.isPercentage = this.percentageOptions[0];
        this.rule.percentageBase = this.latePercentageBaseOptions[3];
        this.rule.calculationMethod = this.lateCalculationMethodOptions[0];
        this.rule.currencyId = null;
        this.rule.amount = 0;
        this.lateConfig = defaultLateConfig();
      } else if (value) {
        this.rule.isPercentage = this.percentageOptions[1];
        this.rule.calculationMethod = this.calculationMethodOptions[1];
      }
      this.syncPercentageFieldValidation();
    },
    'rule.isPercentage'(value) {
      if (value?.param === 'yes') {
        this.rule.currencyId = null;
      }
      if (this.isLateDeduction && value?.param !== 'yes') {
        this.lateConfig.payGradeIds = [];
      }
    },
    'rule.percentageBase'(value) {
      if (this.isLateDeduction && value?.param !== 'pay_grades') {
        this.lateConfig.payGradeIds = [];
      }
    },
    'rule.calculationMethod'(value) {
      if (this.isTaxDeduction && value?.param === 'ter') {
        this.rule.amount = 0;
        this.loadActiveTerSchedule();
      }
    },
  },
  created() {
    this.rule.isPercentage = this.percentageOptions[1];
    this.rule.isActive = this.activeOptions[0];
    this.rule.percentageBase = this.percentageBaseOptions[0];
    this.rule.calculationMethod = this.calculationMethodOptions[1];
    this.syncPercentageFieldValidation();
  },
  beforeMount() {
    this.isLoading = true;
    Promise.all([
      this.http.request({
        method: 'GET',
        url: '/api/v2/payroll/currencies',
        params: {limit: 0},
      }),
      this.payGradeHttp.request({method: 'GET', params: {limit: 0}}),
    ])
      .then(([currencyRes, payGradeRes]) => {
        const currencies = currencyRes.data?.data ?? [];
        this.currencies = currencies.map((item) => ({
          id: item.id,
          label: item.id + ' - ' + item.name,
        }));
        const payGrades = payGradeRes.data?.data ?? [];
        this.payGradeOptions = payGrades.map((pg) => ({
          id: pg.id,
          label: pg.name,
        }));
      })
      .finally(() => {
        this.isLoading = false;
      });
  },
  methods: {
    pt(key) {
      return payrollT(this.$t, key);
    },
    syncPercentageFieldValidation() {
      const type = this.rule.deductionType?.param;
      this.rules.percentage = type === 'tax' ? [] : [required, validSelection];
    },
    buildLateConfigPayload() {
      const payGradeIds = (this.lateConfig.payGradeIds || []).map((item) =>
        typeof item === 'object' ? item.id : item,
      );
      return {
        graceEndMinutes: this.lateConfig.graceEndMinutes ?? 30,
        halfDayMinutesLate: this.lateConfig.halfDayMinutesLate ?? 121,
        bands: this.lateConfig.bands ?? [],
        payGradeIds,
        payGradeNominalSource:
          this.lateConfig.payGradeNominalSource ?? 'calculated',
      };
    },
    nonNpwpMultiplierFromPercentInput() {
      const p = parseFloat(this.rule.nonNpwpSurchargePercent);
      const pct = Number.isFinite(p) ? p : 0;
      return this.round2(1 + pct / 100);
    },
    round2(n) {
      return Math.round(n * 100) / 100;
    },
    formatTerNominal(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return value === null || value === undefined || value === ''
          ? ''
          : String(value);
      }
      return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(n);
    },
    formatTerUpperBound(row) {
      const v = row.upperBound ?? row.upper_bound;
      if (v === null || v === undefined || v === '') {
        return '∞';
      }
      return this.formatTerNominal(v);
    },
    formatTerRatePercent(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) {
        return value === null || value === undefined ? '' : String(value);
      }
      return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      }).format(n);
    },
    loadActiveTerSchedule() {
      const today = new Date().toISOString().slice(0, 10);
      return this.terHttp
        .request({
          method: 'GET',
          params: {
            activeOn: today,
          },
        })
        .then((response) => {
          const list = response?.data?.data ?? [];
          this.activeTerSchedule = list[0] ?? null;
        })
        .catch(() => {
          this.activeTerSchedule = null;
        });
    },
    onCancel() {
      navigate('/payroll/viewPayrollGlobalDeductionRules');
    },
    onSave() {
      this.isLoading = true;
      const isTax = this.rule.deductionType?.param === 'tax';
      const isLate = this.rule.deductionType?.param === 'late';
      const isTaxTer = isTax && this.rule.calculationMethod?.param === 'ter';
      const isPct = this.rule.isPercentage?.param === 'yes';
      const payload = {
        deductionName: this.rule.deductionName,
        deductionType: this.rule.deductionType?.param,
        amount:
          isTaxTer || (isLate && isPct && this.isLateTiered)
            ? 0
            : this.rule.amount,
        currencyId: isPct ? null : this.rule.currencyId?.id ?? null,
        isPercentage: isPct,
        isActive: this.rule.isActive?.param === 'yes',
        comments: this.rule.comments,
        percentageBase:
          isTax || isLate || isPct
            ? this.rule.percentageBase?.param ?? 'gross'
            : 'gross',
        calculationMethod: isTax
          ? this.rule.calculationMethod?.param ?? 'flat'
          : isLate
          ? this.rule.calculationMethod?.param ?? 'tiered'
          : 'flat',
        nonNpwpRateMultiplier: isTax
          ? this.nonNpwpMultiplierFromPercentInput()
          : 1,
        taxBrackets: [],
      };
      if (isLate) {
        payload.lateConfig = this.buildLateConfigPayload();
      }
      this.http
        .create(payload)
        .then(() => this.$toast.updateSuccess())
        .then(() => {
          this.onCancel();
        })
        .catch((response) => {
          return this.$toast.warn({
            title: this.$t('general.warning'),
            message: response?.data.error.message,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-button-row {
  gap: 0.4rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  max-width: 120px;
  margin-left: 60px;

  ::v-deep(.oxd-button--medium) {
    width: 100%;
  }

  @include oxd-respond-to('md') {
    margin-left: unset;
    max-width: unset;
    ::v-deep(.oxd-button--medium) {
      width: unset;
    }
  }
}

$orangehrm-ter-font: system-ui, -apple-system, 'Segoe UI', Roboto,
  'Helvetica Neue', Arial, sans-serif;

.orangehrm-ter-preview {
  font-family: $orangehrm-ter-font;
  margin-bottom: 1rem;
}

.orangehrm-ter-preview-row {
  padding-bottom: 0.75rem;
}

.orangehrm-ter-preview__name {
  font-size: 0.9375rem;
  font-weight: 600;
  margin: 0 0 0.25rem;
  color: $oxd-interface-gray-color;
}

.orangehrm-ter-preview__range {
  font-size: 0.8125rem;
  margin: 0;
  color: $oxd-interface-gray-color !important;
  display: inline;
}

.orangehrm-ter-preview__empty {
  font-size: 0.875rem;
  margin: 0;
  color: $oxd-secondary-font-color;
}

.orangehrm-ter-preview-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: start;

  @include oxd-respond-to('md') {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.orangehrm-ter-preview-grid__cell {
  min-width: 0;
}

.orangehrm-ter-table__caption {
  caption-side: top;
  font-family: $orangehrm-ter-font;
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: left;
  padding: 0.5rem 0.75rem 0.5rem 0.75rem;
  margin: 0;
  color: $oxd-interface-gray-color;
  line-height: 1.35;
  white-space: normal;
}

.orangehrm-ter-preview-table-wrap {
  border-radius: 8px;
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.orangehrm-ter-table {
  width: 100%;
  border-collapse: collapse;
  font-family: $orangehrm-ter-font;
  font-size: 0.8125rem;
}

.orangehrm-ter-table thead th {
  background: $oxd-background-light-gray-color;
  font-weight: 600;
  text-align: left;
  color: $oxd-interface-gray-color;
  border: none;
  border-bottom: 1px solid $oxd-interface-gray-lighten-2-color;
  padding: 0.625rem 0.75rem;
}

.orangehrm-ter-table tbody td {
  border: none;
  border-bottom: 1px solid $oxd-interface-gray-lighten-2-color;
  padding: 0.5rem 0.75rem;
  color: $oxd-interface-gray-color;
}

.orangehrm-ter-table tbody tr:nth-child(even) {
  background: rgba($oxd-interface-gray-lighten-2-color, 0.35);
}

.orangehrm-ter-table tbody tr:last-child td {
  border-bottom: none;
}

.orangehrm-ter-table-empty {
  text-align: center;
  font-size: 0.8125rem;
  color: $oxd-secondary-font-color;
  padding: 0.75rem !important;
}

.orangehrm-form-field-half-lg {
  width: 100%;
  min-width: 0;

  @include oxd-respond-to('lg') {
    width: 50%;
    max-width: 50%;
  }
}

.orangehrm-form-field-half-lg--late-schedule {
  padding-bottom: 1rem;
}
</style>
